// Parser CSV des exports Airbnb + logique d'import (plan + apply).
//
// parseAirbnbCsv(text) → { rows, warnings }
//     Transforme le CSV brut en lignes typées.
//
// buildImportPlan(data, rows, opts) → { toUpdate, toCreate, ignored, summary }
//     Calcule le plan d'import SANS muter data. Pure fonction sérialisable.
//
// applyImportPlan(data, plan, opts) → newData
//     Applique le plan à un data (deep clone). Ne persiste rien — l'appelant
//     (App.jsx) gère saveData().
//
// Contrat parser :
//   parseAirbnbCsv(text) → { rows, warnings }
//     rows     : array de lignes typées (cf. structure ci-dessous)
//     warnings : [{ line: number, reason: string }] — lignes malformées ignorées
//
// Chaque ligne typée :
//   { raw, type, date, airbnbCode, dateReservation, checkIn, checkOut,
//     nuits, voyageur, logement, details, devise, montant, verse,
//     fraisService, fraisMenage, revenusBruts }
//
// Le parser ne fait AUCUN filtrage métier. Il restitue toutes les lignes
// (Réservation, Payout, Versement de résolution, Crédit divers). La
// séparation "à importer vs à ignorer" est la responsabilité de
// buildImportPlan (étape suivante).
//
// Format source Airbnb :
//   - UTF-8 avec BOM (\uFEFF en tête)
//   - Séparateur : virgule
//   - Champs contenant une virgule : quotés avec guillemets doubles
//   - Dates : MM/DD/YYYY (format US — à convertir en YYYY-MM-DD)
//   - Décimales : point (ex : 153.34)
//   - 21 colonnes (cf. HEADER_COLUMNS)

const HEADER_COLUMNS = [
  'Date', 'Arrivée au plus tard le', 'Type', 'Code de confirmation',
  'Date de réservation', 'Date de début', 'Date de fin', 'Nuits', 'Voyageur',
  'Logement', 'Détails', 'Code de référence', 'Devise', 'Montant', 'Versé',
  'Frais de service', 'Frais de Versement sur carte bancaire', 'Frais de ménage',
  'Revenus bruts', 'Taxes reversées par Airbnb', 'Année des revenus',
]

// Split d'une ligne CSV en respectant les "..." (qui peuvent contenir des
// virgules internes, cf. "Transfert vers Dimitri Thomar, IBAN 0007 (EUR)").
// Gère aussi l'échappement "" (guillemet doublé).
function splitCsvLine(line) {
  const out = []
  let cur = ''
  let inQuote = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuote) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++ }
        else { inQuote = false }
      } else {
        cur += ch
      }
    } else {
      if (ch === '"') inQuote = true
      else if (ch === ',') { out.push(cur); cur = '' }
      else cur += ch
    }
  }
  out.push(cur)
  return out
}

// "04/17/2026" → "2026-04-17". Retourne null si format invalide ou vide.
function parseUsDate(s) {
  if (!s) return null
  const m = String(s).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (!m) return null
  const [, mm, dd, yyyy] = m
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`
}

// "153.34" → 153.34. null si vide ou non-numérique.
function parseNumber(s) {
  if (s == null || s === '') return null
  const n = parseFloat(String(s).trim())
  return isNaN(n) ? null : n
}

// « Frais de service » arrive avec une VIRGULE décimale dans les vrais relevés
// (« 27,44 », champ entre guillemets), quand les autres montants ont un point.
// parseFloat lisait 27. Accepte la virgule SEULEMENT sous la forme « chiffres,
// 1 ou 2 chiffres » (jamais un séparateur de milliers). Réservé à cette
// colonne : les autres montants gardent parseNumber, à l'identique.
function parseNumberVirgule(s) {
  if (s == null) return null
  const t = String(s).trim()
  if (/^-?\d+,\d{1,2}$/.test(t)) return parseFloat(t.replace(',', '.'))
  return parseNumber(t)
}

function parseAirbnbCsv(text) {
  const warnings = []
  const rows = []
  if (!text) return { rows, warnings }

  const clean = text.replace(/^\uFEFF/, '')
  const lines = clean.split(/\r?\n/).filter(l => l.trim().length > 0)
  if (lines.length === 0) return { rows, warnings }

  const header = splitCsvLine(lines[0]).map(c => c.trim())
  const idx = {}
  HEADER_COLUMNS.forEach(h => { idx[h] = header.indexOf(h) })

  if (idx['Type'] === -1 || idx['Montant'] === -1 || idx['Code de confirmation'] === -1) {
    warnings.push({ line: 1, reason: `header_invalid (colonnes manquantes parmi Type/Montant/Code de confirmation — format Airbnb modifié ?)` })
    return { rows, warnings }
  }

  // Référence « G-… » du dernier Payout lu : ses lignes Réservation le
  // suivent dans le relevé (forme réelle vérifiée sur les exports Airbnb).
  let refPayoutCourant = null
  // Blocs Payout → Réservations, pour le contrôle « somme = Versé » (tour 5,
  // point 3 : un fichier retrié ou incomplet ne doit pas inventer de
  // versement).
  const blocs = []
  let blocCourant = null
  for (let i = 1; i < lines.length; i++) {
    const cols = splitCsvLine(lines[i])
    if (cols.length < 10) {
      warnings.push({ line: i + 1, reason: `too_few_columns (${cols.length} < 10)` })
      continue
    }

    const get = (h) => {
      const j = idx[h]
      return j >= 0 && j < cols.length ? cols[j] : ''
    }

    const typeLigne = (get('Type') || '').trim()
    if (typeLigne === 'Payout') {
      // Identifiant du versement : la référence « G-… » ; à défaut (13
      // relevés réels sur 14 n'en ont pas — décision 2a du 06/10) « date du
      // Payout|montant Versé ». Sans date ni montant : aucun identifiant.
      const ref = (get('Code de référence') || '').trim()
      const datePayout = parseUsDate(get('Date'))
      const verse = parseNumber(get('Versé'))
      refPayoutCourant = ref ||
        (datePayout && verse != null ? `${datePayout}|${verse.toFixed(2)}` : null)
      blocCourant = { verse, montants: 0, lignes: [] }
      blocs.push(blocCourant)
    } else if (typeLigne !== 'Réservation') {
      // Une autre ligne (résolution…) ferme le bloc du Payout précédent.
      refPayoutCourant = null
      blocCourant = null
    }
    rows.push({
      raw: i + 1,
      type: typeLigne,
      // Identifiant du versement (lignes Réservation) : référence du Payout
      // qui les précède ; null s'il n'y en a pas.
      versementRef: typeLigne === 'Réservation' ? refPayoutCourant : null,
      date: parseUsDate(get('Date')),
      airbnbCode: (get('Code de confirmation') || '').trim() || null,
      dateReservation: parseUsDate(get('Date de réservation')),
      checkIn: parseUsDate(get('Date de début')),
      checkOut: parseUsDate(get('Date de fin')),
      nuits: parseNumber(get('Nuits')),
      voyageur: (get('Voyageur') || '').trim(),
      logement: (get('Logement') || '').trim(),
      details: (get('Détails') || '').trim(),
      devise: (get('Devise') || '').trim(),
      montant: parseNumber(get('Montant')),
      verse: parseNumber(get('Versé')),
      fraisService: parseNumberVirgule(get('Frais de service')),
      fraisMenage: parseNumber(get('Frais de ménage')),
      revenusBruts: parseNumber(get('Revenus bruts')),
    })
  }

  // Tour 5 (point 3) : sous un Payout, la somme des Réservations qui le
  // suivent doit retomber sur son Versé ; sinon tout le bloc perd son
  // identifiant (et passe par le refus face à une réservation déjà comptée).
  {
    let bloc = null
    let idx = 0
    for (const r of rows) {
      if (r.type === 'Payout') { bloc = blocs[idx++] || null; continue }
      if (r.type !== 'Réservation') { bloc = null; continue }
      if (bloc) { bloc.montants += Number(r.montant) || 0; bloc.lignes.push(r) }
    }
    for (const b of blocs) {
      if (b.verse == null || Math.abs(b.montants - b.verse) > 0.01) {
        for (const r of b.lignes) r.versementRef = null
      }
    }
  }

  return { rows, warnings }
}

// Détecte le propriétaire depuis le champ Détails d'un Payout.
// "Transfert vers Dimitri Thomar, IBAN 0007 (EUR)" → "Dimitri Thomar"
function detectOwner(rows) {
  for (const r of rows) {
    if (r.type !== 'Payout') continue
    const m = (r.details || '').match(/Transfert vers\s+(.+?),\s*IBAN/i)
    if (m) return m[1].trim()
  }
  return null
}

// ─────────────────────────────────────────────────────────────────────────────
// Construction et application du plan d'import
// ─────────────────────────────────────────────────────────────────────────────

const { AIRBNB_MAP, resolveListingId } = require('./airbnb-map')
// Plan B (ADR-182) — bucket par majorité de nuits + garde plancher création
// + clé identité stable pour la dédup cross-bucket. Source = copie src/lib
// (ESM transpilée Babel, require interop OK ; require('../../lib/...') interdit
// par CRA car hors de src/).
const {
  computeInvoiceMonthByNights,
  canCreateMonth,
  resaIdentityKey,
} = require('./resa-invoice-month')
// Maison Zen (décision Dimitri 03/10) : marqueur d'origine CSV + date d'import.
const { ORIGINE_CSV_AIRBNB , moisEffectifDuCode } = require('./csv-bascule')
// Parts par versement (séjours payés en plusieurs fois, point A relecture).
const { ajouterPart, fusionnerParts, sommeParts, sommeNegatives, partsValides, PART_CORRECTION, dejaFactureAilleurs, dedoublonnerPartsDatees } = require('./csv-versements')
const _partAirbnb = (r) => ({
  // Date du versement : affichée, ne sert PAS d'identifiant (décision 06/10).
  date: r.date || null,
  montant: r.montant || 0,
  fraisMenage: r.fraisMenage || 0,
  fraisService: r.fraisService,
  revenusBruts: r.revenusBruts,
})

// Date plancher par défaut. Toute résa avec checkIn < DATE_PLANCHER_DEFAULT
// est ignorée. L'utilisateur peut la surcharger via opts.datePlancher.
const DATE_PLANCHER_DEFAULT = '2026-01-01'

const monthFromDate = (isoDate) => (isoDate ? isoDate.slice(0, 7) : null)

// Plan B — index cross-bucket des résas d'un logement par clé identité stable.
// Parcourt TOUS les buckets 'YYYY-MM' de data → Map(key → mk) (premier hit).
// Sert à rattacher un réimport à l'endroit où la résa vit déjà (préserve un
// éventuel month_override posé manuellement), au lieu de la recréer.
function _buildCrossBucketIndex(data, logId) {
  const idx = new Map()
  if (!data) return idx
  for (const mk of Object.keys(data)) {
    if (!/^\d{4}-\d{2}$/.test(mk)) continue
    const resas = data[mk]?.[logId]?.reservations
    if (!Array.isArray(resas)) continue
    for (const r of resas) {
      const key = resaIdentityKey(r)
      if (key && !idx.has(key)) idx.set(key, mk)
    }
  }
  return idx
}

// Helper local — lit les codes Airbnb tombstones pour un logement donné.
// Fallback rétrocompat : si `data._tombstones[lid].airbnb_codes` absent
// → Set vide (comportement inchangé). Cf. plan fix iCal 2026-05-23 (B-3).
function _getDeletedAirbnbCodes(data, lid) {
  const arr = data?._tombstones?.[lid]?.airbnb_codes
  if (!Array.isArray(arr)) return new Set()
  return new Set(arr.map(t => t.airbnbCode).filter(Boolean))
}

const _r2 = (n) => Math.round((Number(n) || 0) * 100) / 100

// Statistiques (champs ORIGINE, comme transformV2Resa et les manuelles) :
//   ca_brut_origine  brut hôte de la réservation
//   ota_commission   frais de service Airbnb (commission hôte)
//   montant_origine  net hôte importé (= montant, inchangé)
// Identité tenue : ca_brut_origine − montant − taxe(0) = ota_commission.
// Brut = Montant + Frais de service : dans les vrais relevés « Revenus bruts »
// vaut ce brut jusqu'en juin 2026, puis un montant plus bas depuis juillet
// (122,09 pour 102,56 + 23,44 = 126,00) — il ne sert qu'à défaut de frais de
// service. Aucune des deux colonnes → aucun champ (les statistiques gardent
// leur repli « brut = net »), jamais un 0 inventé.
function _origineStatsAirbnb(resa) {
  const montant = Number(resa.montant) || 0
  if (resa.fraisService != null && Number.isFinite(Number(resa.fraisService))) {
    const ota = _r2(resa.fraisService)
    return { ca_brut_origine: _r2(montant + ota), ota_commission: ota, montant_origine: _r2(montant) }
  }
  if (resa.revenusBruts != null && Number.isFinite(Number(resa.revenusBruts))) {
    const brut = _r2(resa.revenusBruts)
    return { ca_brut_origine: brut, ota_commission: _r2(brut - montant), montant_origine: _r2(montant) }
  }
  return {}
}

function buildImportPlan(data, rows, opts = {}) {
  const datePlancher = opts.datePlancher || DATE_PLANCHER_DEFAULT
  const airbnbMap = opts.airbnbMap || AIRBNB_MAP
  // Chambres facturées par la V2 (décision Dimitri 03/10) : le rapprochement
  // par date d'arrivée n'y écrase plus une ligne (ancien import iCal à 0 €,
  // sans code) — seul le code HM rapproche. Défaut : aucune.
  const v2Lids = new Set(Array.isArray(opts.v2Lids) ? opts.v2Lids : [])
  // D1 : (logId, mois) → true si le mois est déjà facturé (import refusé).
  const moisGele = typeof opts.moisGele === 'function' ? opts.moisGele : null
  // D (relecture R1) : (code) → { lid, mk } si la V2 de ce code est dans un
  // mois facturé — import refusé même si le mois du relevé est libre.
  const codeGeleV2 = typeof opts.codeGeleV2 === 'function' ? opts.codeGeleV2 : null

  const toUpdate = []
  const toCreate = []
  const ignored = []
  // Remboursements sur une réservation déjà comptée (point B relecture).
  const remboursements = []
  // Tour 3 point 2 : corrections à la main gardées comme une part (annoncées).
  const correctionsManuelles = []
  const reasons = {}
  const bumpReason = (k) => { reasons[k] = (reasons[k] || 0) + 1 }

  // ── Étape 1 : filtrage brut + agrégation par airbnbCode ──
  // Un même airbnbCode peut apparaître sur plusieurs lignes Réservation
  // (versements fractionnés, cf. Bernard De Sousa HMFWDC28A4). On somme
  // montant et fraisMenage ; on garde les autres champs de la 1re occurrence.
  const agg = new Map()

  for (const r of rows) {
    if (r.type !== 'Réservation') {
      bumpReason('type_' + (r.type || 'vide').toLowerCase().replace(/\s+/g, '_'))
      ignored.push({
        lines: [r.raw], airbnbCode: r.airbnbCode, logement: r.logement,
        voyageur: r.voyageur, checkIn: r.checkIn,
        reason: 'type:' + (r.type || 'vide'),
      })
      continue
    }
    if (r.devise !== 'EUR') {
      bumpReason('devise_non_eur')
      ignored.push({
        lines: [r.raw], airbnbCode: r.airbnbCode, logement: r.logement,
        voyageur: r.voyageur, checkIn: r.checkIn,
        reason: 'devise:' + r.devise,
      })
      continue
    }
    if (!r.airbnbCode) {
      bumpReason('code_airbnb_manquant')
      ignored.push({
        lines: [r.raw], airbnbCode: null, logement: r.logement,
        voyageur: r.voyageur, checkIn: r.checkIn,
        reason: 'code_airbnb_manquant',
      })
      continue
    }

    const prev = agg.get(r.airbnbCode)
    if (prev) {
      prev.montant += (r.montant || 0)
      prev.fraisMenage += (r.fraisMenage || 0)
      if (r.fraisService != null) prev.fraisService = (prev.fraisService || 0) + r.fraisService
      if (r.revenusBruts != null) prev.revenusBruts = (prev.revenusBruts || 0) + r.revenusBruts
      // Parts par versement : toutes les lignes doivent porter leur date,
      // sinon comportement d'avant (aucune part).
      prev.parts = prev.parts && r.versementRef ? ajouterPart(prev.parts, r.versementRef, _partAirbnb(r)) : null
      prev.rawLines.push(r.raw)
    } else {
      agg.set(r.airbnbCode, {
        airbnbCode: r.airbnbCode,
        checkIn: r.checkIn,
        checkOut: r.checkOut,
        nuits: r.nuits,
        voyageur: r.voyageur,
        logement: r.logement,
        montant: r.montant || 0,
        fraisMenage: r.fraisMenage || 0,
        // Statistiques : null = colonne absente ou vide (jamais 0 inventé).
        fraisService: r.fraisService != null ? r.fraisService : null,
        revenusBruts: r.revenusBruts != null ? r.revenusBruts : null,
        // Versement = référence « G-… » du Payout qui précède la ligne
        // (décision Dimitri 06/10 ; la date n'identifie pas : deux versements
        // le même jour existent). Sans Payout associé : aucun identifiant.
        parts: r.versementRef ? ajouterPart({}, r.versementRef, _partAirbnb(r)) : null,
        rawLines: [r.raw],
      })
    }
  }

  // ── Étape 2 : pour chaque résa agrégée, classer en update / create / ignore ──
  // Plan B — index cross-bucket par logement (cache) : retrouve une résa
  // existante quel que soit son bucket actuel (préserve un month_override).
  const crossBucketIndexCache = new Map()
  const getCrossBucketIndex = (logId) => {
    if (!crossBucketIndexCache.has(logId)) {
      crossBucketIndexCache.set(logId, _buildCrossBucketIndex(data, logId))
    }
    return crossBucketIndexCache.get(logId)
  }

  for (const resa of agg.values()) {
    if (!resa.checkIn || resa.checkIn < datePlancher) {
      bumpReason('avant_date_plancher')
      ignored.push({
        lines: resa.rawLines, airbnbCode: resa.airbnbCode, logement: resa.logement,
        voyageur: resa.voyageur, checkIn: resa.checkIn,
        reason: 'avant_date_plancher:' + datePlancher,
      })
      continue
    }
    // Annulation / remboursement : une réservation dont la SOMME des montants
    // est ≤ 0 est écartée et rapportée (jamais créée à 0 € ni en négatif, et
    // une ligne déjà importée n'est pas écrasée) — comme Booking écarte un
    // net ≤ 0. La somme, pas la ligne : une part à 0 € d'un versement
    // fractionné ne fait rien écarter.
    // AVEC parts de versement : décidé après rapprochement (un remboursement
    // sur une réservation comptée est une part négative qui s'y soustrait).
    if (!resa.parts && !(Number(resa.montant) > 0)) {
      bumpReason('montant_non_positif')
      ignored.push({
        lines: resa.rawLines, airbnbCode: resa.airbnbCode, logement: resa.logement,
        voyageur: resa.voyageur, checkIn: resa.checkIn,
        reason: 'montant_non_positif',
      })
      // Point B : remboursement sur une réservation déjà comptée → signalé
      // (le montant compté ne bouge pas sans identifiant de versement).
      const lidR = Number(resa.montant) < 0 ? resolveListingId(resa.logement, airbnbMap) : null
      const mkR = lidR ? getCrossBucketIndex(lidR).get('ab:' + resa.airbnbCode) : null
      const compteeR = mkR ? (data[mkR][lidR].reservations || []).find(x => x && x.airbnbCode === resa.airbnbCode) : null
      if (compteeR && Number(compteeR.montant) > 0) {
        remboursements.push({ code: resa.airbnbCode, lid: lidR, rembourse: _r2(resa.montant), compte: _r2(compteeR.montant) })
      }
      continue
    }

    const logId = resolveListingId(resa.logement, airbnbMap)
    if (!logId) {
      bumpReason('logement_non_mappe')
      ignored.push({
        lines: resa.rawLines, airbnbCode: resa.airbnbCode, logement: resa.logement,
        voyageur: resa.voyageur, checkIn: resa.checkIn,
        reason: 'logement_non_mappe:' + resa.logement,
      })
      continue
    }

    // Plan B — bucket cible = mois de MAJORITÉ DE NUITS (ADR-182), pas le mois
    // de check-in. Fallback monthFromDate si helper null (checkIn manquant).
    const naturalMonth = computeInvoiceMonthByNights(resa.checkIn, resa.checkOut)
      || monthFromDate(resa.checkIn)

    // Dédup cross-bucket : si cette résa (clé identité ab:<code>) vit déjà dans
    // un AUTRE bucket → on la met à jour LÀ où elle est (préserve un éventuel
    // month_override posé manuellement via ◀▶). Sinon → bucket naturel.
    const crossIdx = getCrossBucketIndex(logId)
    const identKey = resa.airbnbCode ? 'ab:' + resa.airbnbCode : null
    const existingBucket = identKey ? crossIdx.get(identKey) : null
    const month = existingBucket || naturalMonth

    // D1 (décision Dimitri 03/10) : mois déjà facturé (facture voyageur V
    // émise sur ce logement × mois) → ni création ni mise à jour, motif dit.
    // Relecture tour 3 (point 4) : le gel teste le mois EFFECTIF (déplacement
    // de mois _monthOverrides compris), pas seulement le mois de rangement.
    const moisEffectif = moisEffectifDuCode(data, logId, resa.airbnbCode, month)
    const gelMois = moisGele ? moisGele(logId, moisEffectif) : false
    if (gelMois) {
      // Motif nommé : facture de l'app, ou mois facturé hors app (tour 4).
      const motifGel = (gelMois && gelMois.motif) || 'mois_facture'
      bumpReason(motifGel)
      ignored.push({
        lines: resa.rawLines, airbnbCode: resa.airbnbCode, logement: resa.logement,
        voyageur: resa.voyageur, checkIn: resa.checkIn, logId,
        reason: motifGel + ':' + moisEffectif,
      })
      continue
    }

    const geleV2 = codeGeleV2 ? codeGeleV2(resa.airbnbCode) : null
    if (geleV2) {
      bumpReason('mois_facture')
      ignored.push({
        lines: resa.rawLines, airbnbCode: resa.airbnbCode, logement: resa.logement,
        voyageur: resa.voyageur, checkIn: resa.checkIn, logId: geleV2.lid,
        reason: 'mois_facture:' + geleV2.mk,
      })
      continue
    }

    const existingResas = data?.[month]?.[logId]?.reservations || []
    const deletedAirbnbSet = _getDeletedAirbnbCodes(data, logId)

    // Priorité : match par airbnbCode (stable). Fallback : match par checkIn.
    let match = existingResas.find(r => r.airbnbCode && r.airbnbCode === resa.airbnbCode)
    // Secours par date : seulement une ligne AIRBNB (comme Booking exige
    // 'booking') — jamais une réservation Booking ou directe du même jour.
    if (!match && !v2Lids.has(logId)) match = existingResas.find(r => (r.checkIn || r.date) === resa.checkIn && String(r.plateforme || '').toLowerCase() === 'airbnb')

    // Point A : parts par versement — fusion avec celles déjà enregistrées
    // (un versement vu remplace sa part), montant = somme des parts.
    let eff = resa
    let partsFinales = null
    // Relecture tour 3 (point 3) : une ligne SANS identifiant de versement ne
    // remplace jamais une réservation déjà détaillée par versements — refusée,
    // motif visible, montant inchangé.
    if (!resa.parts && match && partsValides(match.parts_versements)) {
      bumpReason('versement_sans_identifiant')
      ignored.push({
        lines: resa.rawLines, airbnbCode: resa.airbnbCode, logement: resa.logement,
        voyageur: resa.voyageur, checkIn: resa.checkIn, logId,
        reason: 'versement_sans_identifiant',
      })
      continue
    }
    // Tour 5 (décision du 06/10, « 1 ok ») — garde « déjà facturé
    // ailleurs » TOUJOURS, avec ou sans détail de versements : avant toute
    // création, et avant toute mise à jour d'une réservation sans détail (ou
    // à 0 €). Une ligne d'un mois GELÉ du même logement, même période
    // (arrivée + nuits) et même montant → refus nommé ; prénoms différents
    // (les deux connus) → « à trancher », jamais libre.
    const sansDetail = !(match && partsValides(match.parts_versements))
    const cibleGarde = sansDetail ? {
      exclureId: match ? match.id : null, voyageur: resa.voyageur,
      checkIn: resa.checkIn, checkOut: resa.checkOut, nuits: resa.nuits,
      montant: resa.parts ? (sommeParts(resa.parts, 'montant') || 0) : _r2(resa.montant),
    } : null
    if (cibleGarde && moisGele) {
      const dejaFacture = dejaFactureAilleurs(data, logId, cibleGarde, moisGele)
      if (dejaFacture) {
        // deja_facture | a_trancher_deja_facture | a_trancher_chevauchement
        const motif = dejaFacture.motif || 'deja_facture'
        bumpReason(motif)
        ignored.push({
        lines: resa.rawLines, airbnbCode: resa.airbnbCode, logement: resa.logement,
        voyageur: resa.voyageur, checkIn: resa.checkIn, logId,
        reason: motif + ':' + dejaFacture.mk + ':' + (dejaFacture.numero || '?'),
      })
        continue
      }
    }
    // Décision Dimitri 06/10 (« ok refus », remplace la règle 3b ; tour 5 :
    // sans identifiant de versement aussi) : réservation existante SANS
    // détail de versements, montant ≠ 0, qui reçoit un versement → refus,
    // impossible de savoir s'il est déjà compté ; rien n'est ajouté ni
    // remplacé. Une ligne à 0 € reçoit normalement le versement.
    const ancienMontant = match && sansDetail ? Number(match.montant) : 0
    if (match && sansDetail && Number.isFinite(ancienMontant) && Math.abs(ancienMontant) > 0.005) {
      bumpReason('avant_suivi_versements')
      ignored.push({
        lines: resa.rawLines, airbnbCode: resa.airbnbCode, logement: resa.logement,
        voyageur: resa.voyageur, checkIn: resa.checkIn, logId,
        reason: 'avant_suivi_versements', versements: resa.parts ? Object.keys(resa.parts).map((k) => ({ date: resa.parts[k].date || null, montant: resa.parts[k].montant })) : [{ date: resa.date || null, montant: _r2(resa.montant) }],
      })
      continue
    }
    if (resa.parts) {
      let dejaParts = match && partsValides(match.parts_versements) ? match.parts_versements : null
      // Relecture tour 3 (point 2) : montant corrigé à la main (écart > 0,01
      // avec la somme du détail) → l'écart est GARDÉ comme une part
      // « correction manuelle » et annoncé ; jamais perdu en silence.
      if (dejaParts) {
        const ecart = _r2(Number(match.montant) - (sommeParts(dejaParts, 'montant') || 0))
        // Tour 4 (point 4) : ménage corrigé à la main, même règle.
        const ecartMenage = match.menage != null
          ? _r2(Number(match.menage) - (sommeParts(dejaParts, 'fraisMenage') || 0)) : 0
        const corrMontant = Number.isFinite(ecart) && Math.abs(ecart) > 0.01
        const corrMenage = Number.isFinite(ecartMenage) && Math.abs(ecartMenage) > 0.01
        if (corrMontant || corrMenage) {
          dejaParts = ajouterPart(JSON.parse(JSON.stringify(dejaParts)), PART_CORRECTION, {
            montant: corrMontant ? ecart : 0,
            ...(corrMenage ? { fraisMenage: ecartMenage } : {}),
          })
          correctionsManuelles.push({ code: resa.airbnbCode, lid: logId, ecart: corrMontant ? ecart : 0,
            ...(corrMenage ? { ecartMenage } : {}) })
        }
      }
      // Tour 6 : part « G-… » ↔ part « date|versé » du même versement :
      // jamais additionnées (même règle que Booking).
      if (dejaParts) {
        const dd = dedoublonnerPartsDatees(dejaParts, resa.parts, 'montant')
        partsFinales = fusionnerParts(dd.existantes, dd.nouvelles)
      } else {
        partsFinales = resa.parts
      }
      eff = {
        ...resa,
        montant: sommeParts(partsFinales, 'montant') || 0,
        fraisMenage: sommeParts(partsFinales, 'fraisMenage') || 0,
        fraisService: sommeParts(partsFinales, 'fraisService'),
        revenusBruts: sommeParts(partsFinales, 'revenusBruts'),
      }
      if (!match && !(eff.montant > 0)) {
        bumpReason('montant_non_positif')
        ignored.push({
          lines: resa.rawLines, airbnbCode: resa.airbnbCode, logement: resa.logement,
          voyageur: resa.voyageur, checkIn: resa.checkIn,
          reason: 'montant_non_positif',
        })
        continue
      }
      // Tour 4 (point 1) : correction à la main (présente ou détectée) ET
      // remboursement dans ce relevé → on ne sait pas lequel l'emporte : refus,
      // « à trancher », montant inchangé.
      const rembourse = sommeNegatives(resa.parts, 'montant')
      const correctionPresente = !!(dejaParts && dejaParts[PART_CORRECTION] &&
        Math.abs(Number(dejaParts[PART_CORRECTION].montant) || 0) > 0.01)
      if (match && rembourse < 0 && correctionPresente) {
        correctionsManuelles.pop()
        bumpReason('a_trancher_correction_remboursement')
        ignored.push({
        lines: resa.rawLines, airbnbCode: resa.airbnbCode, logement: resa.logement,
        voyageur: resa.voyageur, checkIn: resa.checkIn, logId,
        reason: 'a_trancher_correction_remboursement',
      })
        continue
      }
      if (match && rembourse < 0) {
        remboursements.push({ code: resa.airbnbCode, lid: logId, rembourse, compte: _r2(match.montant) })
      }
      // Tour 4 (point 1) : JAMAIS négatif, ligne détaillée comprise.
      if (match && eff.montant < 0) {
        bumpReason('montant_non_positif')
        ignored.push({
          lines: resa.rawLines, airbnbCode: resa.airbnbCode, logement: resa.logement,
          voyageur: resa.voyageur, checkIn: resa.checkIn,
          reason: 'montant_non_positif',
        })
        continue
      }
    }
    const origine = _origineStatsAirbnb(eff)
    const versements = partsFinales ? { parts_versements: partsFinales } : {}

    if (match) {
      const oldMontant = match.montant || 0
      // Une mensualité qui s'ajoute n'est pas un écrasement de montant.
      const overwritingManual = !partsValides(match.parts_versements) && !partsFinales &&
        oldMontant > 0 && Math.abs(oldMontant - eff.montant) > 0.01
      toUpdate.push({
        logId, month, moisEffectif,
        // Tour 5 (point 5) : refaite au clic sur Confirmer, factures relues.
        ...(cibleGarde ? { gardeDejaFacture: cibleGarde } : {}),
        existingResaId: match.id,
        airbnbCode: resa.airbnbCode,
        old: { montant: oldMontant, menage: match.menage || 0 },
        new: {
          montant: eff.montant,
          menage: eff.fraisMenage,
          checkIn: resa.checkIn,
          checkOut: resa.checkOut,
          nuits: resa.nuits,
          voyageur: resa.voyageur,
          airbnbCode: resa.airbnbCode,
          ...origine,
          ...versements,
        },
        overwritingManual,
        rawLines: resa.rawLines,
      })
    } else {
      // Fix B-3 (2026-05-23) : skip si airbnbCode dans tombstones (admin a
      // supprimé cette résa et ne souhaite pas la voir réinsérée).
      if (deletedAirbnbSet.has(resa.airbnbCode)) {
        bumpReason('tombstone_airbnb_code')
        ignored.push({
          lines: resa.rawLines, airbnbCode: resa.airbnbCode, logement: resa.logement,
          voyageur: resa.voyageur, checkIn: resa.checkIn,
          reason: 'tombstone_airbnb_code:' + resa.airbnbCode,
        })
        continue
      }
      // Plan B — clamp CREATE : pas de création de résa sur un mois invoice
      // ≤ avril 2026 (canCreateMonth). N'affecte PAS les UPDATE (édition d'un
      // existant en avril reste permise).
      if (!canCreateMonth(month)) {
        bumpReason('invoice_month_floor')
        ignored.push({
          lines: resa.rawLines, airbnbCode: resa.airbnbCode, logement: resa.logement,
          voyageur: resa.voyageur, checkIn: resa.checkIn,
          reason: 'invoice_month_floor:' + month,
        })
        continue
      }
      toCreate.push({
        logId, month, moisEffectif,
        ...(cibleGarde ? { gardeDejaFacture: cibleGarde } : {}),
        resa: {
          voyageur: resa.voyageur,
          plateforme: 'airbnb',
          checkIn: resa.checkIn,
          date: resa.checkIn,
          nuits: resa.nuits || 1,
          montant: eff.montant,
          menage: eff.fraisMenage,
          taxe: 0,
          notes: 'Import CSV Airbnb',
          airbnbCode: resa.airbnbCode,
          ...origine,
          ...versements,
        },
        rawLines: resa.rawLines,
      })
    }
  }

  return {
    toUpdate, toCreate, ignored,
    // Point B : [{ code, lid, rembourse (<0), compte }] — texte à l'écran.
    remboursements,
    // Tour 3 point 2 : [{ code, lid, ecart, total }] — texte à l'écran.
    corrections: correctionsManuelles.map((c) => {
      const u = toUpdate.find((x) => x.logId === c.lid && (x.airbnbCode || x.bookingRef) === c.code)
      return { ...c, total: u ? u.new.montant : null, ...(c.ecartMenage != null && u ? { menage: u.new.menage } : {}) }
    }).filter((c) => c.total != null),
    summary: {
      totalRows: rows.length,
      toUpdateCount: toUpdate.length,
      toCreateCount: toCreate.length,
      ignoredCount: ignored.length,
      reasons,
    },
  }
}

function applyImportPlan(data, plan, opts = {}) {
  const nd = JSON.parse(JSON.stringify(data || {}))
  // Chaque création / mise à jour porte l'origine CSV et la date d'import
  // (décision Dimitri 03/10 : savoir quel jour les relevés sont complets).
  const importeLe = opts.importeLe || new Date().toISOString()

  const ensureEntry = (month, logId) => {
    if (!nd[month]) nd[month] = {}
    if (!nd[month][logId]) nd[month][logId] = { reservations: [], extras: [] }
    if (!nd[month][logId].reservations) nd[month][logId].reservations = []
    if (!nd[month][logId].extras) nd[month][logId].extras = []
  }

  const genId = (airbnbCode) =>
    'csv_' + airbnbCode + '_' + Math.random().toString(36).slice(2, 8)

  for (const u of plan.toUpdate) {
    ensureEntry(u.month, u.logId)
    const resas = nd[u.month][u.logId].reservations
    const idx = resas.findIndex(r => r.id === u.existingResaId)
    if (idx < 0) continue
    const keepOldMontant = opts.preserveExistingMontants && u.old.montant > 0
    resas[idx] = {
      ...resas[idx],
      airbnbCode: u.airbnbCode,
      // Toute résa avec airbnbCode HMxxx est nécessairement Airbnb.
      // Force l'alignement pour corriger les résas iCal antérieures
      // qui auraient pu atterrir en plateforme 'direct'.
      plateforme: 'airbnb',
      voyageur: u.new.voyageur || resas[idx].voyageur,
      checkIn: u.new.checkIn,
      date: u.new.checkIn,
      nuits: u.new.nuits || resas[idx].nuits,
      montant: keepOldMontant ? resas[idx].montant : u.new.montant,
      menage: u.new.menage != null ? u.new.menage : resas[idx].menage,
      // Statistiques : brut / commission / net d'origine du relevé (absents
      // du plan si le relevé ne les porte pas → valeurs existantes gardées).
      ...(u.new.ca_brut_origine != null ? {
        ca_brut_origine: u.new.ca_brut_origine,
        ota_commission: u.new.ota_commission,
        montant_origine: u.new.montant_origine,
      } : {}),
      ...(u.new.parts_versements ? { parts_versements: u.new.parts_versements } : {}),
      origine_import: ORIGINE_CSV_AIRBNB,
      importe_le: importeLe,
    }
  }

  for (const c of plan.toCreate) {
    ensureEntry(c.month, c.logId)
    nd[c.month][c.logId].reservations.push({
      ...c.resa,
      id: genId(c.resa.airbnbCode),
      origine_import: ORIGINE_CSV_AIRBNB,
      importe_le: importeLe,
    })
  }

  return nd
}

module.exports = {
  HEADER_COLUMNS,
  splitCsvLine,
  parseUsDate,
  parseNumber,
  parseAirbnbCsv,
  detectOwner,
  DATE_PLANCHER_DEFAULT,
  buildImportPlan,
  applyImportPlan,
}
